import type { Country, Level2PlatformData } from '../../types';
import type { Product } from '../../pages/CatalogPage';
import { CountryColumnHeaders } from './CountryColumnHeaders';

type TabTableHeaderProps = {
  activeTab: string;
  tabSearchQuery: string;
  selectedPlatform: string | null;
  browseMode: boolean;
  selectedCountry: string;
  COUNTRIES: Country[];
  unifiedVisibleCountries: Country[];
  searchVisibleCountries: Country[];
  hardwareProducts: Product[];
  softwareProducts: Product[];
  lbm2Products: Product[];
  level2Platforms: Record<string, Level2PlatformData>;
};

function SimpleTableHeader({
  firstColWidth,
  browseMode,
  visibleCountries,
  selectedCountry,
  COUNTRIES,
}: {
  firstColWidth: string;
  browseMode: boolean;
  visibleCountries: Country[];
  selectedCountry: string;
  COUNTRIES: Country[];
}) {
  return (
    <table className="w-full table-fixed">
      <thead className="bg-transparent">
        <tr className="h-16">
          <th
            className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent"
            style={{ width: firstColWidth }}
          />
          <CountryColumnHeaders
            browseMode={browseMode}
            visibleCountries={visibleCountries}
            selectedCountry={selectedCountry}
            COUNTRIES={COUNTRIES}
          />
        </tr>
      </thead>
    </table>
  );
}

export function TabTableHeader({
  activeTab,
  tabSearchQuery,
  selectedPlatform,
  browseMode,
  selectedCountry,
  COUNTRIES,
  unifiedVisibleCountries,
  searchVisibleCountries,
  hardwareProducts,
  softwareProducts,
  lbm2Products,
  level2Platforms,
}: TabTableHeaderProps) {
  if (tabSearchQuery.trim() && selectedPlatform !== 'Integrated Payments') {
    return (
      <SimpleTableHeader
        firstColWidth="300px"
        browseMode={browseMode}
        visibleCountries={searchVisibleCountries}
        selectedCountry={selectedCountry}
        COUNTRIES={COUNTRIES}
      />
    );
  }

  if (activeTab === 'supported-hardware' && hardwareProducts.length > 0) {
    return (
      <SimpleTableHeader
        firstColWidth="300px"
        browseMode={browseMode}
        visibleCountries={unifiedVisibleCountries}
        selectedCountry={selectedCountry}
        COUNTRIES={COUNTRIES}
      />
    );
  }

  if (activeTab === 'product-modules' && softwareProducts.length > 0) {
    return (
      <SimpleTableHeader
        firstColWidth="200px"
        browseMode={browseMode}
        visibleCountries={unifiedVisibleCountries}
        selectedCountry={selectedCountry}
        COUNTRIES={COUNTRIES}
      />
    );
  }

  if (activeTab === 'supported-payments') {
    if (
      selectedPlatform === 'Shift4 Dine' ||
      selectedPlatform === 'Shift4 Duo' ||
      selectedPlatform === 'Shift4 One'
    ) {
      return null; // DinePaymentsTable renders its own header
    }
    return (
      <SimpleTableHeader
        firstColWidth="200px"
        browseMode={browseMode}
        visibleCountries={unifiedVisibleCountries}
        selectedCountry={selectedCountry}
        COUNTRIES={COUNTRIES}
      />
    );
  }

  if (activeTab === 'level3-lbm-2.0' && lbm2Products.length > 0) {
    return (
      <SimpleTableHeader
        firstColWidth="200px"
        browseMode={browseMode}
        visibleCountries={unifiedVisibleCountries}
        selectedCountry={selectedCountry}
        COUNTRIES={COUNTRIES}
      />
    );
  }

  if (activeTab.startsWith('level2-')) {
    const platformKey =
      activeTab === 'level2-payments'
        ? 'Shift4 Payments'
        : Object.keys(level2Platforms).find(
            key => `level2-${key.toLowerCase().replace(/\s+/g, '-')}` === activeTab
          );

    if (!platformKey || !level2Platforms[platformKey]) return null;

    const visibleCountries =
      activeTab === 'level2-payments' ? COUNTRIES : unifiedVisibleCountries;

    return (
      <SimpleTableHeader
        firstColWidth="200px"
        browseMode={browseMode}
        visibleCountries={visibleCountries}
        selectedCountry={selectedCountry}
        COUNTRIES={COUNTRIES}
      />
    );
  }

  if (activeTab.startsWith('vertical-') && selectedPlatform === 'Integrated Payments') {
    return (
      <table className="w-full table-fixed">
        <thead className="bg-transparent">
          <tr className="h-16">
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[180px]">Partner</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[180px]">Solution</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[120px]">Hardware Family</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[100px]">Integration Type</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[80px]">Version</th>
            <th className="text-center px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[100px]">
              <div className="flex flex-col items-center gap-0.5">
                <span>Shift4 Duo</span>
                <span className="text-xs text-muted-foreground font-normal">(Pay / Order at the Table)</span>
              </div>
            </th>
            <CountryColumnHeaders
              browseMode={browseMode}
              visibleCountries={unifiedVisibleCountries}
              selectedCountry={selectedCountry}
              COUNTRIES={COUNTRIES}
            />
          </tr>
        </thead>
      </table>
    );
  }

  return null;
}
